'use client';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useI18n, useDirection } from '@/i18n';

// Layer data with visual representation
const layerVisuals = [
  { number: '01', lines: 3 },
  { number: '02', lines: 4 },
  { number: '03', lines: 5 },
  { number: '04', lines: 4 },
  { number: '05', lines: 3 },
];

function LayerIndicator({ index, isActive }: { index: number; isActive: boolean }) {
  return (
    <div className="flex items-center gap-3">
      {/* Number */}
      <span
        className={`font-mono text-xs tabular-nums transition-colors duration-300 ${isActive ? 'text-primary' : 'text-muted-foreground/50'
          }`}
      >
        {layerVisuals[index].number}
      </span>

      {/* Lines visualization */}
      <div className="flex items-center gap-1">
        {[...Array(layerVisuals[index].lines)].map((_, i) => (
          <motion.div
            key={i}
            className={`h-4 transition-colors duration-300 ${isActive ? 'bg-primary' : 'bg-border'
              }`}
            style={{ width: 2 }}
            animate={
              isActive
                ? {
                  scaleY: [1, 1.5, 1],
                  transition: {
                    duration: 0.6,
                    delay: i * 0.1,
                    repeat: Infinity,
                    repeatDelay: 1.5,
                  },
                }
                : { scaleY: 1 }
            }
          />
        ))}
      </div>
    </div>
  );
}

function LayerCard({
  layer,
  index,
  isActive,
  onClick,
}: {
  layer: { title: string; description: string };
  index: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      onClick={onClick}
      className={`w-full text-start p-6 rounded-xl border transition-all duration-300 ${isActive
          ? 'bg-card border-primary/20 shadow-sm'
          : 'bg-transparent border-transparent hover:bg-muted/50'
        }`}
      whileHover={{ x: 4 }}
    >
      <div className="flex items-start gap-4">
        <LayerIndicator index={index} isActive={isActive} />

        <div className="flex-1">
          <h3
            className={`text-base font-medium mb-1 transition-colors ${isActive ? 'text-foreground' : 'text-muted-foreground'
              }`}
          >
            {layer.title}
          </h3>
          <p
            className={`text-sm transition-colors ${isActive ? 'text-muted-foreground' : 'text-muted-foreground/60'
              }`}
          >
            {layer.description}
          </p>
        </div>
      </div>
    </motion.button>
  );
}

// Sophisticated system diagram
function SystemDiagram({ activeIndex }: { activeIndex: number }) {
  const { t } = useI18n();
  const d = t.problem.diagram;
  const layers = [
    { label: d.layers.execution },
    { label: d.layers.intelligence },
    { label: d.layers.capacity },
    { label: d.layers.synthesis },
    { label: d.layers.prediction },
  ];

  return (
    <div className="relative aspect-square max-w-md mx-auto">
      {/* Concentric rings */}
      <svg viewBox="0 0 400 400" className="w-full h-full">
        {/* Background rings */}
        {[160, 130, 100, 70, 40].map((r, i) => (
          <circle
            key={i}
            cx="200"
            cy="200"
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            className={`transition-colors duration-500 ${i === activeIndex
                ? 'text-primary/30'
                : 'text-border'
              }`}
          />
        ))}

        {/* Active ring highlight */}
        <motion.circle
          cx="200"
          cy="200"
          r={160 - activeIndex * 30}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-primary"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.6 }}
          key={activeIndex}
        />

        {/* Radial lines */}
        {[0, 60, 120, 180, 240, 300].map((angle, i) => {
          const x2 = 200 + 160 * Math.cos((angle * Math.PI) / 180);
          const y2 = 200 + 160 * Math.sin((angle * Math.PI) / 180);
          return (
            <line
              key={i}
              x1="200"
              y1="200"
              x2={x2}
              y2={y2}
              stroke="currentColor"
              strokeWidth="1"
              className="text-border"
              strokeDasharray="4 4"
            />
          );
        })}

        {/* Data points on active ring */}
        {[0, 72, 144, 216, 288].map((angle, i) => {
          const r = 160 - activeIndex * 30;
          const x = 200 + r * Math.cos((angle * Math.PI) / 180);
          const y = 200 + r * Math.sin((angle * Math.PI) / 180);
          return (
            <motion.circle
              key={i}
              cx={x}
              cy={y}
              r="6"
              fill="currentColor"
              className="text-primary"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: i * 0.1 }}
            />
          );
        })}

        {/* Animated pulse on active ring */}
        <motion.circle
          cx="200"
          cy="200"
          r={160 - activeIndex * 30}
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          className="text-primary"
          initial={{ scale: 1, opacity: 0.5 }}
          animate={{ scale: 1.1, opacity: 0 }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      </svg>

      {/* Layer labels - centered with background */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="text-center bg-background px-6 py-4 rounded-xl"
          >
            <div className="text-sm font-mono text-primary mb-1">
              {d.layerLabel} {String(activeIndex + 1).padStart(2, '0')}
            </div>
            <div className="text-lg font-medium text-foreground">
              {layers[activeIndex].label}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export function Architecture() {
  const { t } = useI18n();
  const [activeIndex, setActiveIndex] = useState(0);

  const layers = [
    t.problem.layers.interaction,
    t.problem.layers.dissatisfaction,
    t.problem.layers.capacity,
    t.problem.layers.performance,
    t.problem.layers.intelligence,
  ];

  // Auto-rotate
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % 5);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="about" className="py-24 sm:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm font-medium text-primary mb-4"
          >
            {t.problem.badge}
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl font-display font-medium text-foreground mb-4"
          >
            {t.problem.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted-foreground"
          >
            {t.problem.subtitle}
          </motion.p>
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Layers list */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-2"
          >
            {layers.map((layer, index) => (
              <LayerCard
                key={index}
                layer={layer}
                index={index}
                isActive={index === activeIndex}
                onClick={() => setActiveIndex(index)}
              />
            ))}
          </motion.div>

          {/* Visualization */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:ps-8"
          >
            <SystemDiagram activeIndex={activeIndex} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
